import { jsx as _jsx } from "react/jsx-runtime";
/**
 * ThemeProvider — dark/light theme switching via CSS custom properties.
 *
 * Sets `data-theme` on `<html>`, which activates the matching CSS variable
 * block defined in `apps/web/src/index.css`.
 *
 * COPPA-safe: does NOT write to localStorage for under-13 users without
 * verified parental consent. Falls back to React state only.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
const ThemeContext = createContext(null);
const STORAGE_KEY = 'k_theme';
/**
 * Detect system preference via `prefers-color-scheme` media query.
 */
function getSystemTheme() {
    if (typeof window === 'undefined')
        return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/**
 * Read stored theme preference from localStorage.
 * Returns null if not available or storage is blocked.
 */
function readStoredTheme() {
    if (typeof window === 'undefined')
        return null;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'dark' || stored === 'light' || stored === 'system') {
            return stored;
        }
    }
    catch {
        // localStorage unavailable (private browsing, storage quota, etc.)
    }
    return null;
}
/**
 * Write theme preference to localStorage.
 * COPPA-safe: callers should gate on consent before calling.
 */
function writeStoredTheme(mode, canPersist) {
    if (!canPersist)
        return;
    if (typeof window === 'undefined')
        return;
    try {
        localStorage.setItem(STORAGE_KEY, mode);
    }
    catch {
        // Silently fail — state is still in React
    }
}
export function ThemeProvider({ children, defaultMode = 'system', canPersist = true, pin, }) {
    const [themeMode, setThemeModeState] = useState(() => {
        return readStoredTheme() ?? defaultMode;
    });
    // Resolve 'system' → actual theme
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);
    const resolvedTheme = themeMode === 'system' ? systemTheme : themeMode;
    // Listen for system theme changes
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
            setSystemTheme(e.matches ? 'dark' : 'light');
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    // Apply data-theme to <html>
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', pin ?? resolvedTheme);
    }, [resolvedTheme, pin]);
    const setThemeMode = useCallback((mode) => {
        setThemeModeState(mode);
        writeStoredTheme(mode, canPersist);
    }, [canPersist]);
    const toggleTheme = useCallback(() => {
        const next = resolvedTheme === 'dark' ? 'light' : 'dark';
        setThemeMode(next);
    }, [resolvedTheme, setThemeMode]);
    const value = useMemo(() => ({
        resolvedTheme,
        themeMode,
        setThemeMode,
        toggleTheme,
    }), [resolvedTheme, themeMode, setThemeMode, toggleTheme]);
    return _jsx(ThemeContext.Provider, { value: value, children: children });
}
/**
 * Access the current theme context.
 * Must be used within a `<ThemeProvider>`.
 */
export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within a <ThemeProvider>');
    }
    return ctx;
}
//# sourceMappingURL=ThemeProvider.js.map